import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { TeamRole } from '../../../../../../typings/TeamMember.interface';
import { withStaticProperties } from '../../../utils/withStaticProperties';
import { usePermissionAwareDisabled } from '../../contexts/TeamPermissionContext';
import StyledEllipsisOverflow from '../helpers/EllipsisOverflow';
import { getStyledUtils, StyledUtilsProps } from '../helpers/StyledUtils';
import Icon, { IconName, StyledIcon } from './Icon';
import Text from './Text';
import Divider from './Divider';

interface Props extends StyledUtilsProps {
  label: React.ReactNode;
  label_icon?: IconName;
  value?: string;
  title?: string;
  icon?: 'add' | 'close';
  mono?: boolean;
  disabled?: boolean;
  active?: boolean;
  dashed?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onIconClick?: (e: React.MouseEvent) => void;
  to?: string | Partial<Location> | ((location: Location) => Partial<Location> | string);
  hide_chevron?: boolean;
}

const StyledFilterChip = styled.button<Omit<Props, 'label' | 'label_icon' | 'icon' | 'onClick'>>(
  ({ theme, mono, disabled, active, dashed }) => css`
    ${getStyledUtils}
    height: ${theme.pxToRem(20)};
    position: relative;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
    line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
    background-color: ${theme.colors.surface.base.surface};
    border-radius: ${theme.radius.small};
    border: none;
    padding: 0;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    width: fit-content;
    max-width: 100%;
    overflow: hidden;

    ${mono &&
    css`
      font-family:
        JetBrains Mono,
        monospace;
      font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
      font-weight: ${theme.font_weights.medium};
    `}

    a, a > div {
      text-decoration: none;
      max-width: 100%;
      font-size: ${theme.pxToRem(theme.font_sizes[mono ? 'xs' : 's'].font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes[mono ? 'xs' : 's'].line_height)};
      color: ${theme.colors.on.neutral.primary_neutral};
    }

    ${disabled &&
    css`
      background-color: ${theme.colors.surface.base.disabled};
      cursor: not-allowed;

      ${StyledIcon} {
        cursor: not-allowed !important;
      }
    `}

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px ${dashed ? 'dashed' : 'solid'} ${theme.colors.outline.neutral};
      border-radius: ${theme.radius.small};
      pointer-events: none;

      ${(active || disabled) &&
      !dashed &&
      css`
        border: 1px solid ${theme.colors.outline.hover.neutral};
      `}
    }

    ${active &&
    css`
      background-color: ${theme.colors.surface.base.hover.neutral};
    `}

    span:not(:first-child) {
      border-left: 1px solid ${theme.colors.outline.neutral};
    }

    &:hover:not(:disabled) {
      background-color: ${theme.colors.surface.base.hover.neutral};
      &::before {
        border-color: ${theme.colors.outline.hover.neutral};
      }
      span:not(:first-child) {
        border-color: ${theme.colors.outline.hover.neutral};
      }
    }
  `,
);

const StyledSplitChipContainer = styled.div<Pick<Props, 'mono' | 'disabled' | 'dashed'>>(
  ({ theme, mono, disabled, dashed }) => css`
    ${getStyledUtils}
    height: ${theme.pxToRem(20)};
    position: relative;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
    line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
    border-radius: ${theme.radius.small};
    width: fit-content;
    max-width: 100%;
    overflow: hidden;

    ${mono &&
    css`
      font-family:
        JetBrains Mono,
        monospace;
      font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
      font-weight: ${theme.font_weights.medium};
    `}

    ${disabled &&
    css`
      ${StyledIcon} {
        cursor: not-allowed !important;
      }
    `}

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px ${dashed ? 'dashed' : 'solid'} ${theme.colors.outline.neutral};
      border-radius: ${theme.radius.small};
      pointer-events: none;
      z-index: 1;
    }

    ${disabled &&
    css`
      &::before {
        border: 1px solid ${theme.colors.outline.hover.neutral};
      }
    `}
  `,
);

const StyledChipPart = styled.button<
  Pick<Props, 'disabled' | 'active'> & { is_icon_part?: boolean }
>(
  ({ theme, disabled, is_icon_part, active }) => css`
    height: 100%;
    display: flex;
    align-items: center;
    background-color: ${disabled
      ? theme.colors.surface.base.disabled
      : theme.colors.surface.base.surface};
    border: none;
    padding: 0;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    position: relative;

    ${is_icon_part &&
    css`
      border-left: 1px solid ${theme.colors.outline.neutral};
    `}

    div {
      white-space: nowrap;
    }

    ${active &&
    !is_icon_part &&
    css`
      background-color: ${theme.colors.surface.base.hover.neutral};
    `}

    &:hover:not(:disabled) {
      background-color: ${theme.colors.surface.base.hover.neutral};

      ${is_icon_part &&
      css`
        border-left-color: ${theme.colors.outline.hover.neutral};
      `}
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.outline.focus.primary};
      outline-offset: 2px;
      z-index: 2;
    }
  `,
);

const FilterChipComponent: React.FC<Props> = ({
  onClick,
  onIconClick,
  label_icon,
  label,
  icon,
  to,
  title,
  disabled,
  value,
  hide_chevron,
  ...props
}) => {
  // When onIconClick is defined and there's an icon, use split layout
  const useSplitLayout = icon && onIconClick;

  const labelContent = (
    <Text
      size="xs"
      title={title}
      semi
      muted={props.dashed}
      ellipsis
      p={{ x: 1 }}
      flex={{ align: 'center' }}>
      {label_icon && (
        <Icon icon={label_icon} left={1} pointer muted={!disabled} disabled={disabled} />
      )}
      <StyledEllipsisOverflow>{label}</StyledEllipsisOverflow>
    </Text>
  );

  const iconContent = icon && (
    <Icon icon={icon} p={0.5} pointer muted={!disabled} disabled={disabled} />
  );

  if (useSplitLayout) {
    return (
      <StyledSplitChipContainer {...props} disabled={disabled} dashed={props.dashed}>
        <StyledChipPart
          type="button"
          active={props.active}
          disabled={disabled}
          onClick={onClick}
          aria-label="chip-label">
          {labelContent}
          <Divider vertical m={{ r: 1, t: 1 }} h={{ px: 12 }} />
          <Text size="xs" semi primary={!!value} muted={!value} m={{ r: 1 }}>
            {value || 'None selected'}
          </Text>
          {!hide_chevron && (
            <Icon icon="chevron_down" p={{ r: 1 }} pointer muted={!disabled} disabled={disabled} />
          )}
        </StyledChipPart>
        <StyledChipPart
          type="button"
          active={props.active}
          disabled={disabled}
          is_icon_part
          onClick={(e) => {
            e.stopPropagation();
            const onClickFn = onIconClick || onClick;
            onClickFn?.(e);
          }}
          aria-label="chip-action">
          {iconContent}
        </StyledChipPart>
      </StyledSplitChipContainer>
    );
  }

  const content = (
    <>
      {labelContent}
      {icon && (
        <Icon
          icon={icon}
          p={0.5}
          pointer
          muted={!props.active}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            const onClickFn = onIconClick || onClick;
            onClickFn?.(e);
          }}
        />
      )}
    </>
  );

  if (to) {
    return (
      <StyledFilterChip disabled={disabled} {...props} as={'div' as any}>
        <Link to={to} onClick={(e) => e.stopPropagation()}>
          {content}
        </Link>
      </StyledFilterChip>
    );
  }

  return (
    <StyledFilterChip
      type="button"
      onClick={onClick}
      dashed={props.dashed}
      disabled={disabled}
      {...props}>
      {content}
    </StyledFilterChip>
  );
};

const FilterChipPermission: React.FC<
  React.ComponentPropsWithRef<typeof FilterChipComponent> & { role?: TeamRole }
> = ({ role, hide_chevron, ...clickableAreaProps }) => {
  const disabled = usePermissionAwareDisabled(clickableAreaProps.disabled, role);

  return (
    <FilterChipComponent
      {...clickableAreaProps}
      disabled={disabled}
      hide_chevron={hide_chevron || false}
    />
  );
};

export default withStaticProperties(FilterChipComponent, { Permission: FilterChipPermission });
